// ============================================================================
// HAI Reporting Tool - Care Level Insights Component
// Visualizes metrics from patient level to provincial level on the dashboard
// ============================================================================

import { useMemo } from 'react';
import { useCareLevelInsights } from '@/hooks/useCareLevelInsights';

function MetricCard({
  label,
  value,
  subLabel,
  tone = 'default'
}: {
  label: string;
  value: string | number;
  subLabel?: string;
  tone?: 'default' | 'success' | 'warning' | 'danger';
}) {
  const palette: Record<'default' | 'success' | 'warning' | 'danger', string> = {
    default: 'bg-gray-50 border-gray-200 text-gray-900',
    success: 'bg-success-50 border-success-200 text-success-900',
    warning: 'bg-warning-50 border-warning-200 text-warning-900',
    danger: 'bg-danger-50 border-danger-200 text-danger-900'
  };

  return (
    <div className={`rounded-xl border p-4 ${palette[tone]} shadow-sm`}>
      <p className="text-xs uppercase tracking-wide text-gray-500">{label}</p>
      <p className="text-2xl font-semibold mt-2">{value}</p>
      {subLabel && <p className="text-xs text-gray-600 mt-1">{subLabel}</p>}
    </div>
  );
}

export function CareLevelInsights() {
  const { insights, loading, error, refetch } = useCareLevelInsights({ days: 30 });
  const patientLevel = insights.patientLevel;
  const wardLevel = insights.wardLevel;
  const facilityLevel = insights.facilityLevel.slice(0, 5);
  const districtLevel = insights.districtLevel.slice(0, 5);
  const provinceLevel = insights.provinceLevel;

  const complianceTone = useMemo(() => {
    if (wardLevel.complianceRate >= 85) return 'success';
    if (wardLevel.complianceRate >= 65) return 'warning';
    return 'danger';
  }, [wardLevel.complianceRate]);

  if (loading) {
    return (
      <div className="card space-y-4">
        <div className="h-4 bg-gray-200 rounded animate-pulse" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(item => (
            <div key={item} className="h-20 bg-gray-100 rounded animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card bg-danger-50 border border-danger-200">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-danger-800 font-semibold">Unable to load care level insights</p>
            <p className="text-sm text-danger-700">{error}</p>
          </div>
          <button className="btn btn-primary" onClick={refetch}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h3 className="text-xl font-semibold text-gray-900">Care Level Navigation</h3>
          <p className="text-sm text-gray-600">
            Aligns dashboard views with the data collected via patient, ward, facility, and provincial forms.
          </p>
        </div>
        <button className="btn btn-secondary" onClick={refetch}>
          Refresh data
        </button>
      </div>

      {/* Patient Level */}
      <section className="card space-y-4">
        <div className="flex items-center justify-between">
          <h4 className="text-lg font-semibold text-gray-900">Patient Level</h4>
          <p className="text-sm text-gray-500">Powered by Patient Detail + HCAI Surveillance forms</p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <MetricCard label="Total Patients" value={patientLevel.totalPatients} />
          <MetricCard
            label="Patients with Devices"
            value={patientLevel.patientsWithDevices}
            subLabel="High-risk due to device exposure"
            tone="warning"
          />
          <MetricCard
            label="Top Infection"
            value={patientLevel.infectionBreakdown[0]?.label || 'No data'}
            subLabel={`${patientLevel.infectionBreakdown[0]?.count || 0} cases`}
          />
          <MetricCard
            label="Dominant Risk Factor"
            value={patientLevel.topRiskFactors[0]?.label || 'Unknown'}
            subLabel={`${patientLevel.topRiskFactors[0]?.count || 0} instances`}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <InsightList
            title="Infection Breakdown"
            emptyLabel="No infections recorded."
            items={patientLevel.infectionBreakdown}
          />
          <InsightList
            title="Risk Factors Observed"
            emptyLabel="No risk factor data captured yet."
            items={patientLevel.topRiskFactors}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <InsightList
            title="Antibiotic Usage (from Surveillance Form)"
            emptyLabel="No antibiotic courses logged."
            items={patientLevel.antibioticUsage}
          />
          <InsightList
            title="Microbiology Findings"
            emptyLabel="No laboratory entries captured."
            items={patientLevel.microbiologyFindings}
          />
        </div>
      </section>

      {/* Ward Level */}
      <section className="card space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="text-lg font-semibold text-gray-900">Ward Level</h4>
            <p className="text-sm text-gray-500">
              Synthesizes daily device counts + weekly HAI + SSI surveillance submissions.
            </p>
          </div>
          <MetricCard
            label="Data Completeness"
            value={`${wardLevel.complianceRate}%`}
            subLabel="Device snapshot adherence last 30 days"
            tone={complianceTone}
          />
        </div>

        <div className="overflow-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500">
                <th className="py-2 pr-4">Ward</th>
                <th className="py-2 pr-4">Facility</th>
                <th className="py-2 pr-4">Compliance</th>
                <th className="py-2 pr-4">CAUTI / CLABSI / VAP</th>
                <th className="py-2">SSI Rate</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {wardLevel.wards.slice(0, 8).map(ward => (
                <tr key={ward.wardId} className="text-gray-900">
                  <td className="py-3 pr-4 font-medium">{ward.wardName}</td>
                  <td className="py-3 pr-4 text-gray-600">{ward.facilityName}</td>
                  <td className="py-3 pr-4">
                    <span className="font-semibold">{ward.compliance}%</span>
                    <span className="text-xs text-gray-500 ml-1">submissions</span>
                  </td>
                  <td className="py-3 pr-4">
                    <TrendChip label="CAUTI" value={ward.utiRate} threshold={2.5} />
                    <TrendChip label="CLABSI" value={ward.cabsiRate} threshold={1.5} />
                    <TrendChip label="VAP" value={ward.vapRate} threshold={2.0} />
                  </td>
                  <td className="py-3">
                    {typeof ward.ssiRate === 'number' ? (
                      <TrendChip label="SSI" value={ward.ssiRate} threshold={3} format="per100" />
                    ) : (
                      <span className="text-xs text-gray-400">No SSI data</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Facility Level */}
      <section className="card space-y-4">
        <h4 className="text-lg font-semibold text-gray-900">Facility & Hospital Level</h4>
        <p className="text-sm text-gray-500">
          Aggregates ward metrics by facility to mirror the admin workflow of approving ward submissions.
        </p>

        <div className="overflow-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500">
                <th className="py-2 pr-4">Facility</th>
                <th className="py-2 pr-4">Region</th>
                <th className="py-2 pr-4">Wards</th>
                <th className="py-2 pr-4">Submission Rate</th>
                <th className="py-2 pr-4">Avg CAUTI</th>
                <th className="py-2 pr-4">Avg CLABSI</th>
                <th className="py-2 pr-4">Avg VAP</th>
                <th className="py-2">High-Risk Wards</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {facilityLevel.map(facility => (
                <tr key={facility.facilityId}>
                  <td className="py-3 pr-4 font-medium text-gray-900">{facility.facilityName}</td>
                  <td className="py-3 pr-4 text-gray-600">{facility.region}</td>
                  <td className="py-3 pr-4">{facility.totalWards}</td>
                  <td className="py-3 pr-4">{facility.submissionRate}%</td>
                  <td className="py-3 pr-4">{facility.avgUtiRate}</td>
                  <td className="py-3 pr-4">{facility.avgCabsiRate}</td>
                  <td className="py-3 pr-4">{facility.avgVapRate}</td>
                  <td className="py-3">{facility.highRiskWards}</td>
                </tr>
              ))}
              {facilityLevel.length === 0 && (
                <tr>
                  <td colSpan={8} className="py-4 text-center text-gray-500">
                    No facility data found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* District Level */}
      <section className="card space-y-4">
        <h4 className="text-lg font-semibold text-gray-900">District & Sub-District Level</h4>
        <p className="text-sm text-gray-500">
          Pulls data directly from the official HCAI Surveillance form (district, hospital, risk factors).
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {districtLevel.map(district => (
            <div key={district.district} className="border rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 uppercase">{district.district}</p>
                  <p className="text-2xl font-semibold text-gray-900">{district.cases} cases</p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-gray-500">Hospitals reporting</p>
                  <p className="text-lg font-semibold">{district.hospitals}</p>
                </div>
              </div>
              <p className="text-sm text-gray-500 mt-2">
                Predominant infection: <span className="font-semibold text-gray-900">{district.predominantInfection || 'n/a'}</span>
              </p>
              <ul className="mt-3 space-y-1">
                {district.riskSignals.map(signal => (
                  <li key={signal.label} className="text-sm text-gray-600 flex justify-between">
                    <span>{signal.label}</span>
                    <span className="font-medium">{signal.count}</span>
                  </li>
                ))}
                {district.riskSignals.length === 0 && (
                  <li className="text-xs text-gray-400">No risk-factor trends logged.</li>
                )}
              </ul>
            </div>
          ))}
          {districtLevel.length === 0 && (
            <p className="text-sm text-gray-500">No district-level HCAI surveillance submissions yet.</p>
          )}
        </div>
      </section>

      {/* Provincial Level */}
      <section className="card space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="text-lg font-semibold text-gray-900">Provincial / National View</h4>
            <p className="text-sm text-gray-500">
              Aggregates facility data by province (facility.region) to support provincial infection control units.
            </p>
          </div>
          <div className="flex gap-3">
            <MetricCard label="National CAUTI" value={`${provinceLevel.nationalRates.uti}/1000`} />
            <MetricCard label="National CLABSI" value={`${provinceLevel.nationalRates.cabsi}/1000`} />
            <MetricCard label="National VAP" value={`${provinceLevel.nationalRates.vap}/1000`} />
            <MetricCard label="National SSI" value={`${provinceLevel.nationalRates.ssi}/100`} subLabel="Per 100 procedures" />
          </div>
        </div>

        <div className="overflow-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500">
                <th className="py-2 pr-4">Province / Region</th>
                <th className="py-2 pr-4">Facilities</th>
                <th className="py-2 pr-4">Wards Reporting</th>
                <th className="py-2 pr-4">Submission Rate</th>
                <th className="py-2 pr-4">Avg CAUTI</th>
                <th className="py-2 pr-4">Avg CLABSI</th>
                <th className="py-2">Avg VAP</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {provinceLevel.summaries.map(region => (
                <tr key={region.region}>
                  <td className="py-3 pr-4 font-medium text-gray-900">{region.region}</td>
                  <td className="py-3 pr-4">{region.facilities}</td>
                  <td className="py-3 pr-4">{region.wards}</td>
                  <td className="py-3 pr-4">{region.submissionRate}%</td>
                  <td className="py-3 pr-4">{region.avgUtiRate}</td>
                  <td className="py-3 pr-4">{region.avgCabsiRate}</td>
                  <td className="py-3">{region.avgVapRate}</td>
                </tr>
              ))}
              {provinceLevel.summaries.length === 0 && (
                <tr>
                  <td colSpan={7} className="py-4 text-center text-gray-500">
                    No facility-region mappings available.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function InsightList({
  title,
  items,
  emptyLabel
}: {
  title: string;
  items: { label: string; count: number }[];
  emptyLabel: string;
}) {
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <p className="text-sm font-semibold text-gray-900">{title}</p>
        {items.length > 0 && (
          <span className="text-xs text-gray-500">{items.length} tracked</span>
        )}
      </div>
      {items.length === 0 ? (
        <p className="text-sm text-gray-500">{emptyLabel}</p>
      ) : (
        <ul className="space-y-2">
          {items.map(item => (
            <li key={item.label} className="flex items-center justify-between text-sm text-gray-700">
              <span>{item.label}</span>
              <span className="font-semibold">{item.count}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function TrendChip({
  label,
  value,
  threshold,
  format = 'per1000'
}: {
  label: string;
  value: number;
  threshold: number;
  format?: 'per1000' | 'per100';
}) {
  const breached = value >= threshold;
  const color = breached ? 'bg-danger-50 text-danger-700 border-danger-200' : 'bg-success-50 text-success-700 border-success-200';
  const unit = format === 'per1000' ? '/1000' : '/100';

  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium mr-2 ${color}`}>
      {label}: {value.toFixed(2)}{unit}
    </span>
  );
}
